import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { consultationService } from '../services/consultationService'
import { userService } from '../services/userService'
import { maternityService } from '../services/maternityService'
import { ConsultationFormData } from '../types/consultation'
import { useAuth } from '../contexts/AuthContext'
import {
  VISIT_REASON_CODES,
  VISIT_REASON_LABELS,
  getVisitReasonLabel,
  isPregnancyRelatedReason,
  isOutcomeWithinMonths,
  POSTNATAL_LOOKBACK_MONTHS,
  type VisitReasonCode,
} from '../constants/visitReasons'
import './QuickStartConsultationForm.css'

interface QuickStartConsultationFormProps {
  patientId: string
  appointmentId?: string // Optional appointment ID if consultation is started from an appointment
  initialStaffId?: string // Optional initial staff ID (e.g., from appointment)
  initialReason?: string // Optional initial reason (e.g., from appointment)
  initialReasonCode?: VisitReasonCode
  initialPregnancyId?: string
  onCancel?: () => void
}

export function QuickStartConsultationForm({ patientId, appointmentId, initialStaffId, initialReason, initialReasonCode, initialPregnancyId, onCancel }: QuickStartConsultationFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const hasSetDefaultProvider = useRef(false)

  const [formData, setFormData] = useState<Partial<ConsultationFormData>>({
    patientId,
    staffId: initialStaffId || '',
    consultationDate: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5),
    status: 'in-progress',
    reason: initialReason || (initialReasonCode ? getVisitReasonLabel(initialReasonCode) : ''),
    reasonCode: initialReasonCode,
    pregnancyId: initialPregnancyId,
  })

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  // Fetch pregnancies when reason is pregnancy-related (for patient detail flow without appointment)
  const { data: pregnancies } = useQuery({
    queryKey: ['pregnancies', patientId],
    queryFn: () => maternityService.getPregnancies(patientId),
    enabled: !!patientId && isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode),
  })

  // Resolve pregnancy when pregnancies load and reason is antenatal/postnatal (patient detail flow)
  useEffect(() => {
    if (!isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) || !pregnancies) return
    let matched: string | null = null
    if (formData.reasonCode === 'antenatal') {
      const active = pregnancies.find((p) => p.status === 'active')
      if (active) matched = active.pregnancyId
    } else if (formData.reasonCode === 'postnatal') {
      const completed = pregnancies
        .filter((p) => p.status === 'completed' && isOutcomeWithinMonths(p.outcomeDate, POSTNATAL_LOOKBACK_MONTHS))
        .sort((a, b) => {
          const aDate = a.outcomeDate || a.updatedAt || ''
          const bDate = b.outcomeDate || b.updatedAt || ''
          return bDate.localeCompare(aDate)
        })[0]
      if (completed) matched = completed.pregnancyId
    }
    setFormData((prev) => ({ ...prev, pregnancyId: matched ?? undefined }))
  }, [formData.reasonCode, pregnancies])

  // Set default provider: first use appointment staffId, then logged-in user if they are a clinical practitioner
  useEffect(() => {
    if (!hasSetDefaultProvider.current && clinicalStaff) {
      // Priority 1: Use staffId from appointment if provided
      if (initialStaffId) {
        const appointmentStaff = clinicalStaff.find((staff) => staff.id === initialStaffId)
        if (appointmentStaff) {
          setFormData((prev) => ({ ...prev, staffId: initialStaffId }))
          hasSetDefaultProvider.current = true
          return
        }
      }
      
      // Priority 2: Use logged-in user if they are a clinical practitioner
      if (user && user.roleCategory === 'Clinical') {
        const currentUserInStaff = clinicalStaff.find((staff) => staff.id === user.id)
        if (currentUserInStaff) {
          setFormData((prev) => ({ ...prev, staffId: user.id }))
          hasSetDefaultProvider.current = true
        }
      }
    }
  }, [user, clinicalStaff, initialStaffId])

  const createMutation = useMutation({
    mutationFn: (data: ConsultationFormData) => consultationService.create(data),
    onSuccess: async (consultation) => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      // Refetch appointments to reflect status change to 'in-progress'
      if (appointmentId) {
        await queryClient.refetchQueries({ queryKey: ['appointments'] })
      }
      // Navigate to consultation workspace
      navigate(`/consultations/${consultation.id}/workspace`)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.staffId) {
      alert('Please select a clinical staff member')
      return
    }
    if (isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) && !formData.pregnancyId) {
      alert(
        formData.reasonCode === 'antenatal'
          ? 'Patient does not have an active pregnancy. Record a pregnancy before starting a consultation.'
          : `Patient does not have a completed pregnancy within ${POSTNATAL_LOOKBACK_MONTHS} months. Record the pregnancy outcome before starting a consultation.`
      )
      return
    }

    try {
      const consultationData: ConsultationFormData = {
        patientId,
        staffId: formData.staffId!,
        appointmentId: appointmentId,
        consultationDate: formData.consultationDate!,
        startTime: formData.startTime,
        status: formData.status!,
        reason: formData.reason || (formData.reasonCode ? getVisitReasonLabel(formData.reasonCode) : ''),
        reasonCode: formData.reasonCode ?? initialReasonCode,
        pregnancyId: formData.pregnancyId ?? initialPregnancyId,
      }
      console.log('[QuickStartConsultationForm] Creating consultation with data:', consultationData)
      await createMutation.mutateAsync(consultationData)
    } catch (error: any) {
      alert(error?.message || 'Failed to create consultation')
    }
  }

  return (
    <form className="quick-start-consultation-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <h2>Start Consultation</h2>
        <p className="form-subtitle">Quick start - you can add details later</p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="staffId">
            Provider <span className="required">*</span>
          </label>
          <select
            id="staffId"
            value={formData.staffId || ''}
            onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
            required
          >
            <option value="">Select provider...</option>
            {clinicalStaff?.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.firstName} {staff.lastName} ({staff.role})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="consultationDate">Date</label>
          <input
            id="consultationDate"
            type="date"
            value={formData.consultationDate}
            onChange={(e) => setFormData({ ...formData, consultationDate: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="startTime">Start Time</label>
          <input
            id="startTime"
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="reasonCode">Reason for Visit (Optional)</label>
        <select
          id="reasonCode"
          value={formData.reasonCode || ''}
          onChange={(e) => {
            const code = (e.target.value || undefined) as VisitReasonCode | undefined
            setFormData((prev) => ({
              ...prev,
              reasonCode: code,
              reason: code === 'other' ? prev.reason : (code ? getVisitReasonLabel(code) : ''),
              pregnancyId: !isPregnancyRelatedReason(code) ? undefined : prev.pregnancyId,
            }))
          }}
        >
          <option value="">Select a reason (optional)</option>
          {VISIT_REASON_CODES.map((code) => (
            <option key={code} value={code}>
              {VISIT_REASON_LABELS[code]}
            </option>
          ))}
        </select>
        {formData.reasonCode === 'other' && (
          <input
            type="text"
            value={formData.reason || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder="Enter reason..."
            className="reason-other-input"
            style={{ marginTop: '0.5rem' }}
          />
        )}
        {isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) && (
          <p className="field-hint">
            {formData.pregnancyId
              ? `Linked to ${formData.reasonCode === 'antenatal' ? 'active' : 'last completed'} pregnancy`
              : formData.reasonCode === 'antenatal'
                ? 'Patient does not have an active pregnancy. Record a pregnancy before starting a consultation.'
                : `Patient does not have a completed pregnancy within ${POSTNATAL_LOOKBACK_MONTHS} months. Record the pregnancy outcome before starting a consultation.`}
          </p>
        )}
      </div>

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={
            createMutation.isPending ||
            (isPregnancyRelatedReason(formData.reasonCode as VisitReasonCode) && !formData.pregnancyId)
          }
        >
          {createMutation.isPending ? 'Starting...' : 'Start Consultation'}
        </button>
      </div>
    </form>
  )
}


























